export const TABS = {
  topup: 'topup',
} as const;

export type ActiveTab = keyof typeof TABS;

export const P2W_TABS = {
  p2wSalesTax: 'p2wSalesTax',
  p2wProfitPerGame: 'p2wProfitPerGame',
} as const;

export type P2WActiveTab = keyof typeof P2W_TABS;

export interface RequestBase {
  page?: number;
  limit?: number;
}

export interface TaxRequestParam extends RequestBase {
  order_type?: string;
  created_by_username?: string;
  business_owner_username?: string;
  business_owner_firstname?: string;
  business_owner_lastname?: string;
  game_ids?: { value: string; label: string }[];
  game_package_ids?: { value: string; label: string }[];
  agent_tier_ids?: { value: string; label: string }[];
  completed_at_from?: string;
  completed_at_to?: string;
  /** Serialized as comma-separated IDs: `supplier_ids=id1,id2` */
  supplier_ids?: (string | { value: string; label: string })[];
  game_topup_type_ids?: string;
  order_no?: string;
  p2w_transaction_id?: string;
  package_name?: string;
}

export interface TopupRequestParam extends RequestBase {
  transaction_no?: string;
  business_owner_first_name?: string;
  business_owner_last_name?: string;
  related_username?: string;
  username?: string;
  status?: string;
  business_name?: string;
  completed_at_from?: string;
  completed_at_to?: string;
  all?: boolean;
  performed_by?: string;
}

export interface GameSaleRequestParam extends RequestBase {
  game_ids?: { value: string; label: string }[];
}

export interface TaxSummary {
  total_orders: number;
  total_sales: number;
  total_cost: number;
  total_net_profit: number;
}


// P2W Report types
export interface P2WSalesTaxReport {
  order_no?: string;
  source?: string;
  order_type?: string;
  game_name?: string;
  package_name?: string;
  topup_type_name?: string;
  player_id?: string;
  original_price?: number;
  selling_price?: number;
  selling_price_inc_vat?: number;
  selling_price_exc_vat?: number;
  vat_amount?: number;
  discount_amount?: number;
  cost?: number;
  p2w_cost?: number;
  p2w_transaction_id?: string;
  net_profit_p2w?: number;
  completed_at?: string;
  business_owner_id?: string;
  business_owner_username?: string;
  business_name?: string;
  agent_tier_name?: string;
  created_by_username?: string;
  supplier_name?: string;
}

export interface P2WProfitReport {
  game_id?: string;
  game_name?: string;
  total_orders?: number;
  total_sales_exc_vat?: number;
  total_p2w_cost?: number;
  gross_profit?: number;
  gross_profit_percent?: number;
}

export interface BusinessProfitRequestParam extends RequestBase {
  completed_at_from?: string;
  completed_at_to?: string;
  game_ids?: { value: string; label: string }[];
  order_no?: string;
  format?: string;
}

export interface BusinessProfitReportItem {
  game_id?: string;
  game_name?: string;
  total_orders?: number;
  total_revenue?: number;
  total_platform_cost?: number;
  gross_profit?: number;
  gross_profit_percent?: number;
}

export interface BusinessProfitSummary {
  total_games?: number;
  total_orders?: number;
  total_revenue?: number;
  total_platform_cost?: number;
  total_gross_profit?: number;
  avg_gross_profit_percent?: number;
}

export interface BusinessProfitPerOrderItem {
  order_no?: string;
  completed_at?: string;
  game_name?: string;
  package_name?: string;
  customer_price?: number;
  price_snapshot?: number;
  profit?: number;
  profit_percent?: number;
}

export interface StaffProfitRequestParam extends RequestBase {
  completed_at_from?: string;
  completed_at_to?: string;
  business_owner_id?: string;
  business_name?: string;
  game_ids?: string[];
  format?: string;
}

export interface StaffProfitReportItem {
  business_owner_id?: string;
  business_name?: string;
  game_id?: string;
  game_name?: string;
  total_orders?: number;
  total_customer_revenue?: number;
  total_platform_cost?: number;
  total_supplier_cost?: number;
  business_profit?: number;
  platform_profit?: number;
  platform_profit_pct?: number;
}

export interface StaffProfitSummary {
  total_businesses?: number;
  total_games?: number;
  total_orders?: number;
  total_customer_revenue?: number;
  total_platform_cost?: number;
  total_supplier_cost?: number;
  total_business_profit?: number;
  total_platform_profit?: number;
}

// Garena summary types
export interface GarenaSalesTaxSummary {
  total_orders: number;
  total_selling_price: number;
  total_garena_cost_thb: number;
  total_net_profit: number;
}

export interface GarenaProfitByGameSummary {
  total_games: number;
  total_orders: number;
  total_sales: number;
  total_garena_cost_thb: number;
  total_gross_profit: number;
  avg_gross_profit_pct: number;
}

// Garena Report types
export const GARENA_TABS = {
  garenaSalesTax: 'garenaSalesTax',
  garenaProfitPerGame: 'garenaProfitPerGame',
} as const;

export type GarenaActiveTab = keyof typeof GARENA_TABS;

export interface GarenaRequestParam extends RequestBase {
  completed_at_from?: string;
  completed_at_to?: string;
  game_ids?: { value: string; label: string }[];
  order_no?: string;
  garena_transaction_id?: string;
  business_name?: string;
  agent_tier_ids?: { value: string; label: string }[];
  game_package_ids?: { value: string; label: string }[];
}

export interface GarenaSalesTaxReport {
  order_no?: string;
  game_name?: string;
  package_name?: string;
  topup_type_name?: string;
  player_id?: string;
  character_name?: string;
  selling_price?: number;
  garena_cost?: number;
  garena_transaction_id?: string;
  shell_balance_before?: number;
  net_profit?: number;
  completed_at?: string;
  business_owner_username?: string;
  business_name?: string;
  agent_tier_name?: string;
}

export interface GarenaProfitReport {
  game_id?: string;
  game_name?: string;
  total_orders?: number;
  total_sales?: number;
  total_garena_cost?: number;
  gross_profit?: number;
  gross_profit_percent?: number;
}

/** Form shape for report search filters (react-hook-form + URL sync). */
export type ReportSearchFilters = {
  status?: string;
  channel?: string;
  game?: string;
  orderType?: string;
  packageName?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  firstName?: string;
  lastName?: string;
  procressedBy?: string;
  transactionNo?: string;
  orderNo?: string;
  gameId?: Array<{ value: string; label: string }>;
  packageId?: Array<{ value: string; label: string }>;
  agentTier?: Array<{ value: string; label: string }>;
  movementType?: Array<{ value: string; label: string }>;
  walletType?: Array<{ value: string; label: string }>;
  walletId?: string;
  userId?: string;
  relatedUsername?: string;
  fullName?: string;
  partnerName?: string;
  username?: string;
  gameCard?: string;
  itemList?: string;
  dealer?: [{ value: string; label: string }];
  /** Multi supplier from SupplierCombobox — buildQueryParams serializes as `supplier_ids=id1,id2` */
  supplierIds?: Array<{ value: string; label: string }>;
  topupTypeId?: string;
  p2wTransactionId?: string;
  garenaTransactionId?: string;
  razerTransactionNo?: string;
  performedBy?: string;
};
